import { useEffect, useContext, useMemo, useState } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  Users,
  Search,
  Plus,
  Save,
  RefreshCcw,
  ChevronDown,
  ChevronUp,
  Hash,
  Type,
} from "lucide-react";

const BASE_URL = "https://nissi-india.com"; // change if needed

const EmployeeTypeMaster = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = localStorage.getItem("finYearId") || "";

  const [showFilters, setShowFilters] = useState(true);

  // form (ASP: flag + typeid)
  const [showForm, setShowForm] = useState(true);
  const [editingId, setEditingId] = useState(0); // 0 = insert, >0 = update
  const [typeCode, setTypeCode] = useState("");
  const [typeName, setTypeName] = useState("");

  // list + search + paging
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // selection (delete)
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);

  const totalPages = useMemo(() => {
    const t = Math.ceil(Number(total || 0) / Number(pageSize || 10));
    return t <= 0 ? 1 : t;
  }, [total, pageSize]);

  const screenBadge = useMemo(() => "INFRA SCREEN : C-4", []);

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const dangerBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ` +
    (selectedIds.length === 0 ? "bg-gray-400 cursor-not-allowed" : "bg-red-600 hover:bg-red-700");

  const ensureFinYear = () => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return false;
    }
    return true;
  };

  // ASP: codegeneration()
  const loadNextCode = async () => {
    if (!ensureFinYear()) return;
    try {
      const res = await axios.get(`${BASE_URL}/api/employee-type/next-code`, {
        params: { finYearId: Number(finYearId) },
      });
      if (res?.data?.status) {
        setTypeCode(res?.data?.data?.code || "");
      } else {
        toast.error(res?.data?.message || "Failed to generate code");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to generate code");
    }
  };

  const fetchList = async (p = page) => {
    if (!ensureFinYear()) return;

    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/employee-type`, {
        params: {
          finYearId: Number(finYearId),
          page: p,
          pageSize,
          search: search.trim(),
        },
      });

      if (res?.data?.status) {
        setRows(res.data.data || []);
        setTotal(res.data.total || 0);
        setPage(res.data.page || p);

        // reset selection like ASP postback refresh
        setSelectedIds([]);
        setSelectAll(false);
      } else {
        toast.error(res?.data?.message || "Failed to load Employee Types.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  // init like Page_Load (!IsPostBack)
  useEffect(() => {
    (async () => {
      await loadNextCode();
      await fetchList(1);
      setPage(1);
      setEditingId(0);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchList(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // ASP clearContent()
  const clearContent = async () => {
    setTypeName("");
    setEditingId(0);
    await loadNextCode();
  };

  // ASP btn_addnew_Click
  const handleAddNew = async () => {
    await clearContent();
    setShowForm(true);
  };

  const handleRefresh = async () => {
    await fetchList(page);
  };

  const handleSearch = async (e) => {
    e?.preventDefault?.();
    setPage(1);
    await fetchList(1);
  };

  // ✅ Clear filter and reload full list
  const handleClearFilters = () => {
    setSearch("");
    setPage(1);
    fetchList(1);
  };

  // ASP isSpacesOnly()
  const validateSpacesOnly = () => {
    const r = String(typeName || "").replace(/\s/g, "");
    if (!r.length) {
      setTypeName("");
      toast.warn("Please Enter Employee Type Name");
      return false;
    }
    return true;
  };

  // ASP ImageButton1_Click => insert/update
  const handleSave = async (e) => {
    e?.preventDefault?.();

    if (!ensureFinYear()) return;
    if (!validateSpacesOnly()) return;

    try {
      setLoading(true);

      const payload = {
        finYearId: Number(finYearId),
        typeid: Number(editingId || 0),
        typecode: String(typeCode || "").trim(),
        typename: String(typeName || "").trim(),
      };

      const res = await axios.post(`${BASE_URL}/api/employee-type`, payload);

      if (res?.data?.status) {
        toast.success(res?.data?.message || (editingId ? "Successfully Updated" : "Successfully Inserted"));
        await clearContent();
        await fetchList(1);
        setPage(1);
      } else {
        toast.error(res?.data?.message || "Already Exists");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  // ASP GridView View command (typename link)
  const handleEdit = async (typeid) => {
    if (!ensureFinYear()) return;

    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/employee-type/${typeid}`, {
        params: { finYearId: Number(finYearId) },
      });

      if (res?.data?.status && res?.data?.data) {
        const row = res.data.data;
        setEditingId(Number(row.typeid || 0));
        setTypeCode(row.typecode || "");
        setTypeName(row.typename || "");
        setShowForm(true);
      } else {
        toast.error(res?.data?.message || "Record not found.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedIds([]);
      setSelectAll(false);
      return;
    }
    setSelectedIds(rows.map((r) => r.typeid));
    setSelectAll(true);
  };

  // ASP ConfirmSubmit + REMOVE
  const handleDeleteSelected = async () => {
    if (!ensureFinYear()) return;

    if (selectedIds.length === 0) {
      toast.warn("Please select any one");
      return;
    }

    const ok = window.confirm("Are you sure to delete?");
    if (!ok) return;

    try {
      setLoading(true);

      const res = await axios.delete(`${BASE_URL}/api/employee-type`, {
        data: { finYearId: Number(finYearId), ids: selectedIds },
      });

      if (res?.data?.status) {
        toast.success(res?.data?.message || "Removed Successfully");
        await clearContent();
      } else {
        toast.error(res?.data?.message || "The selected row has referred in Employee Managment");
      }

      const nextPage = page > 1 && rows.length === selectedIds.length ? page - 1 : page;
      setPage(nextPage);
      await fetchList(nextPage);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const showingFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = Math.min(page * pageSize, total);

  return (
    <PayRollLayoutWrapper>
      <ToastContainer
        position="top-right"
        autoClose={2800}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">EMPLOYEE TYPE MASTER</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* ==================== HEADER ==================== */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div
                className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${
                  isDark ? "bg-white/10" : "bg-gray-100"
                }`}
              >
                <Users className={isDark ? "text-white" : "text-gray-700"} />
              </div>

              <div>
                <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Employee Type Master
                </div>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Add / Edit / Delete / Search / Pagination
                </div>
              </div>

              <div
                className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                  isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {screenBadge}
              </div>

              <div
                className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                  isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800"
                }`}
              >
                {total} Records
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button onClick={handleAddNew} className={primaryBtn} disabled={loading}>
                <Plus className="w-4 h-4" />
                Add New
              </button>

              <button onClick={() => setShowForm((v) => !v)} className={ghostBtn}>
                {showForm ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {showForm ? "Hide Form" : "Show Form"}
              </button>

              <button onClick={() => setShowFilters((v) => !v)} className={ghostBtn}>
                {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {/* ==================== SEARCH FILTERS ==================== */}
          {showFilters && (
            <form onSubmit={handleSearch} className={cardBase + " p-6 mb-6"}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div className="text-lg font-extrabold">Search Criteria</div>

                <div className="flex flex-wrap gap-3">
                  <button type="button" onClick={handleClearFilters} className={ghostBtn} disabled={loading}>
                    Clear
                  </button>

                  <button type="button" onClick={handleRefresh} className={ghostBtn} disabled={loading}>
                    <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                    Refresh
                  </button>

                  <button type="submit" className={primaryBtn} disabled={loading}>
                    <Search className="w-4 h-4" />
                    Search
                  </button>
                </div>
              </div>

              <div>
                <label className={labelBase}>Search (Code / Name)</label>
                <div className="mt-2 relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Search className="w-4 h-4 text-gray-400" />
                  </div>
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Type to search..."
                    className={`pl-11 ${inputBase}`}
                  />
                </div>
              </div>
            </form>
          )}

          {/* ==================== ADD / EDIT FORM ==================== */}
          {showForm && (
            <form onSubmit={handleSave} className={cardBase + " p-6 mb-6"}>
              <div className="text-lg font-extrabold mb-5">
                {editingId ? `Edit Employee Type (ID: ${editingId})` : "New Employee Type"}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className={labelBase}>Employee Type Code</label>
                  <div className="mt-2 flex items-center gap-2">
                    <Hash className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                    <input value={typeCode} readOnly className={inputBase + " cursor-not-allowed opacity-90"} />
                  </div>
                </div>

                <div>
                  <label className={labelBase}>
                    Employee Type Name <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-2 flex items-center gap-2">
                    <Type className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                    <input
                      value={typeName}
                      onChange={(e) => setTypeName(e.target.value)}
                      placeholder="e.g. Permanent"
                      className={inputBase}
                    />
                  </div>
                </div>
              </div>

              {/* Center buttons like ASP */}
              <div className="mt-6 flex flex-wrap gap-3 justify-center">
                <button type="submit" className={primaryBtn} disabled={loading}>
                  <Save className="w-4 h-4" />
                  {editingId ? "Update" : "Save"}
                </button>

                <button type="button" onClick={handleAddNew} className={ghostBtn} disabled={loading}>
                  <Plus className="w-4 h-4" />
                  Add New
                </button>
              </div>
            </form>
          )}

          {/* ==================== RESULTS TABLE ==================== */}
          <div className={cardBase}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${primaryBtnClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-bold">S.No</th>
                    <th className="px-4 py-4 text-center font-bold">Type Code</th>
                    <th className="px-4 py-4 text-center font-bold">Type Name</th>
                    <th className="px-4 py-4 text-center font-bold">
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                        className="w-4 h-4 cursor-pointer"
                        title="Select All"
                      />
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: pageSize }).map((_, idx) => (
                      <tr key={idx} className={isDark ? "bg-gray-900" : "bg-white"}>
                        {Array.from({ length: 4 }).map((__, c) => (
                          <td key={c} className="px-4 py-4">
                            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={4} className={`px-4 py-12 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => (
                      <tr
                        key={r.typeid}
                        className={`${
                          idx % 2 === 0
                            ? isDark ? "bg-gray-800" : "bg-white"
                            : isDark ? "bg-gray-750" : "bg-gray-50"
                        } transition-colors ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}
                      >
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {(page - 1) * pageSize + idx + 1}
                        </td>

                        <td className={`px-4 py-4 text-sm text-center font-medium ${isDark ? "text-blue-400" : "text-blue-600"}`}>
                          {r.dcode}
                        </td>

                        {/* ASP: Type Name is Link -> loads record */}
                        <td className="px-4 py-4 text-sm text-center">
                          <button
                            type="button"
                            onClick={() => handleEdit(r.typeid)}
                            className={`inline-flex items-center gap-1 font-bold underline underline-offset-2 ${
                              isDark ? "text-white hover:text-gray-200" : "text-gray-900 hover:text-gray-700"
                            }`}
                            title="Edit"
                          >
                          {r.typename}
                          </button>
                        </td>

                        <td className="px-4 py-4 text-center">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(r.typeid)}
                            onChange={() => toggleSelect(r.typeid)}
                            className="w-4 h-4 cursor-pointer"
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* ==================== REMOVE BUTTON ==================== */}
            {rows.length > 0 && (
              <div className="p-4 border-t border-gray-200 dark:border-white/10">
                <button
                  type="button"
                  onClick={handleDeleteSelected}
                  disabled={selectedIds.length === 0 || loading}
                  className={dangerBtn}
                >
                  🗑 Remove
                </button>
              </div>
            )}
          </div>

          {/* ==================== PAGINATION ==================== */}
          <div className="flex flex-col md:flex-row items-center justify-between mt-6 gap-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              Showing <b>{showingFrom}</b> to <b>{showingTo}</b> of <b>{total}</b>
            </div>

            {totalPages > 1 && (
              <div className="inline-flex rounded-2xl overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className={`px-4 py-2 text-sm font-bold ${
                    page === 1
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                  }`}
                >
                  Prev
                </button>

                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setPage(i + 1)}
                    className={`px-4 py-2 text-sm font-bold transition ${
                      page === i + 1
                        ? `${primaryBtnClass} text-white`
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}

                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className={`px-4 py-2 text-sm font-bold ${
                    page === totalPages
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                  }`}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default EmployeeTypeMaster;